/**
 * Customer-portal view types for bench runs (GET /api/client/bench/runs and
 * GET /api/client/bench/runs/:id). Read-side DTOs only; the agent wire
 * contract lives in ./contract.ts.
 */
import { z } from 'zod';
import { isUsableSerial } from '../inventory/serial-placeholder.js';
import {
  benchBootToolIdSchema,
  benchDeselectionsByteSize,
  benchDeviceKindSchema,
  benchStageDeselectionSchema,
  MAX_STAGE_DESELECTIONS,
  MAX_STAGE_DESELECTIONS_BYTES,
  type BenchAttestationLevel,
} from './contract.js';
import type { BenchMissingReason } from './device-identity.js';

export type BenchRunStatus = 'running' | 'committed' | 'abandoned';
export type BenchCosmeticGrade = 'A' | 'B' | 'C' | 'D';

export interface BenchRunSummary {
  id: string;
  /** `abandoned` is derived: a running run older than the 24 h session lifetime. */
  status: BenchRunStatus;
  attestationLevel: BenchAttestationLevel;
  functionalBand: string | null;
  chassisSerial: string | null;
  productName: string | null;
  labelPrinted: boolean | null;
  startedAt: string;
  committedAt: string | null;
  /** A wipe ticket was issued (a wipe started) but the run never committed, so no certificate exists. */
  wipedNotCertified: boolean;
}

export interface BenchRunDeviceView {
  deviceIndex: number;
  kind: string;
  deviceKey: string;
  serial: string | null;
  model: string | null;
  /** SIL-2331, additive: manufacturer from discovery (device vendor; chassis falls back to sysVendor). Absent from an API that predates it. */
  vendor?: string | null;
  /**
   * SIL-2482, additive: why `serial` is null (a closed set, see device-identity.ts). Null when a serial is present. Absent from
   * an API that predates it; read absent as "no reason known".
   */
  serialMissingReason?: BenchMissingReason | null;
  /** SIL-2482, additive: why `model` is null. Null when a model is known. */
  modelMissingReason?: BenchMissingReason | null;
  /** SIL-2482, additive: `pci_id` = `model` was resolved from the PCI vendor:device id, not read from the device. */
  modelSource?: 'agent' | 'pci_id' | null;
  /** NVMe with a usable serial and a deviceKey no other drive shares. Only these can be offered for a wipe. */
  wipeEligible: boolean;
  /** null until the run commits. */
  outcome: string | null;
  assetId: string | null;
  computedBand: string | null;
  appliedBand: string | null;
  conditionApplied: boolean | null;
  /**
   * SIL-2000, additive: this device's asset row was recorded under a deselection (bench_run_asset.partial_scope, derived
   * by the database). Absent from an API that predates it; read absent as false.
   */
  partialScope?: boolean;
  /**
   * SIL-2258, additive: this part is not part of the plan approved for the run (out of scope, or it has no resolution row), so it
   * was neither bound to inventory, graded nor wipe-eligible. Absent for a run with no plan.
   */
  outOfScope?: boolean;
}

/** SIL-2258: how one plan step ended on one device. `gap` = no SiliconX test for that part yet; `not-selected` = an org step unticked at approval. */
export type BenchPlanStepState = 'ran-pass' | 'ran-fail' | 'not-run' | 'gap' | 'not-selected';

export interface BenchPlanCoverageStepView {
  /** A catalog step kind (closed vocabulary, never free text). */
  stepKind: string;
  origin: 'mandatory' | 'org';
  state: BenchPlanStepState;
}

export interface BenchPlanCoverageDeviceView {
  deviceIndex: number;
  deviceKey: string;
  kind: string;
  inScope: boolean;
  /** The grade is capped (never like_new): a mandatory step had no test (gap), was not run, or the part has no mandatory test. */
  capped: boolean;
  /** Rendered as "capped: N mandatory steps not tested". */
  mandatoryNotTested: number;
  /** Rendered as "no SiliconX mandatory test for this part". */
  noMandatoryTest: boolean;
  steps: BenchPlanCoverageStepView[];
}

/**
 * SIL-2258 (plan 8, anti-goal 1), additive: what the approved plan asked of the run and how each step ended, built from the stored
 * resolution joined to the stored stage rows (so it cannot drift from what ran). Absent for a run with no plan. Carries the plan
 * owner and version and the mandatory set version, never the free-text name of an org plan.
 */
export interface BenchPlanCoverageView {
  planOwner: string;
  planVersion: number;
  mandatorySetVersion: number;
  /** Parts the plan left out of scope. */
  outOfScope: number;
  devices: BenchPlanCoverageDeviceView[];
}

/**
 * SIL-2502, additive: a planned step that did not run because its plan row resolved to a gap. `reason` is the plan-resolve gap
 * reason stored at approval (null for a run approved before it was stored, or a reason this build does not know).
 */
export interface BenchNotRunStepView {
  deviceIndex: number;
  /** Device kind from the plan ("gpu", "cpu", ...), for wording. */
  deviceKind: string;
  stepKind: string;
  reason: string | null;
}

/** Plain customer wording for a stored gap reason. Unknown or missing reasons get a generic line, never an error. */
export function benchGapReasonLabel(reason: string | null, deviceKind?: string): string {
  const part = deviceKind === 'gpu' ? 'GPU' : 'part';
  switch (reason) {
    case 'no_profile': return `No active test profile for this ${part}`;
    case 'no_stage': return `The test profile for this ${part} has no stage for this step`;
    case 'not_agent_observed': return 'The available test cannot be observed by the bench agent';
    case 'broken_profile': return `The test profile for this ${part} could not be read`;
    case 'identity_mismatch':
    case 'missing_class': return `This ${part} could not be identified well enough to pick a test`;
    case 'unknown_kind':
    case 'inactive_kind':
    case 'kind_area_mismatch': return 'This test is not available';
    case 'not_org_addable':
    case 'not_mandatory_allowed': return 'This test cannot be used in this plan';
    default: return 'Not run';
  }
}

export interface BenchRunStageView {
  seq: number;
  deviceIndex: number;
  stageName: string;
  status: string;
  exitCode: number | null;
  /** SIL-2502: why a non-pass stage failed or did not run (agent-written, length-capped). Null for a pass. */
  errorReason: string | null;
  /** SIL-2502: scrubbed last lines of the stage's output (non-pass stages, agents that send it; at most 4 KB). Null otherwise. */
  outputTail: string | null;
  destructive: boolean;
  /** As reported by the unit; its clock is not trusted (SIL-1853). */
  startedAt: string;
  /** As reported by the unit; its clock is not trusted (SIL-1853). */
  finishedAt: string;
  /** Server time the stage result was received (ISO). The authoritative time of the stage (SIL-1853). */
  receivedAt: string;
}

export interface BenchWipeAuthorizationView {
  deviceKey: string;
  action: 'authorize' | 'revoke';
  byMe: boolean;
  createdAt: string;
}

export interface BenchCertificateView {
  id: string;
  deviceSerial: string | null;
  deviceModel: string | null;
  nistMethod: string;
  technique: string;
  passed: boolean;
  verificationPassed: boolean;
  attestationLevel: BenchAttestationLevel;
  /** As reported by the unit; its clock is not trusted (SIL-1853). */
  finishedAt: string;
  /** Server time the wipe stage result was received (ISO); the authoritative time of the wipe (SIL-1853). */
  receivedAt: string;
}

export interface BenchCosmeticGradeView {
  grade: BenchCosmeticGrade;
  setByMe: boolean;
  createdAt: string;
}

/**
 * SIL-2000. Why a stage on the approval screen cannot be unchecked: the profile requires it, it is a wipe stage
 * (wipe authorization governs it), the agent does not observe it (bmc / uefi), or the device shares its real deviceKey with
 * another discovered device (`shared_device_key`: the opaque key cannot tell the two apart, so the server locks every stage
 * of both; the approve route refuses such a deselection too). A stage that is also required / destructive / not observed keeps that reason.
 */
export const benchStageNotSelectableReasonSchema = z.enum(['required', 'destructive', 'not_agent_observed', 'shared_device_key']);
export type BenchStageNotSelectableReason = z.infer<typeof benchStageNotSelectableReasonSchema>;

export const benchPairingTestStageSchema = z.object({
  stageIndex: z.number().int().nonnegative(),
  name: z.string().max(128),
  required: z.boolean(),
  destructive: z.boolean(),
  selectable: z.boolean(),
  notSelectableReason: benchStageNotSelectableReasonSchema.nullable(),
});

export const benchPairingTestDeviceSchema = z.object({
  /**
   * Opaque `device:<deviceIndex>` for EVERY caller, admins included: the real deviceKey embeds the serial and never leaves the
   * server. Quote it as `deselections[].deviceKey` on approve; the API maps it back from the pairing's own discovery.
   */
  deviceKey: z.string().regex(/^device:(0|[1-9]\d{0,8})$/),
  deviceIndex: z.number().int().nonnegative(),
  kind: benchDeviceKindSchema,
  vendor: z.string().max(128).nullable(),
  model: z.string().max(128).nullable(),
  serial: z.string().max(128).nullable(),
  pciAddress: z.string().max(32).nullable(),
  /** The profile this device is held to (strictest match); null = no profile, no tests run for it. */
  profile: z.object({ id: z.string().max(128), version: z.number().int().positive() }).nullable(),
  stages: z.array(benchPairingTestStageSchema),
});

/**
 * SIL-2000. The test options GET /api/client/bench/pairings/:userCode returns while BENCH_TEST_SELECTION_ENABLED is on:
 * per discovered device, the stages of the profile it matches and which of them the caller may deselect.
 * `canDeselect` is UX only (the caller is an org admin); the approve DEFINER function is the gate.
 */
export const benchPairingTestOptionsSchema = z.object({
  devices: z.array(benchPairingTestDeviceSchema),
  canDeselect: z.boolean(),
});
export type BenchPairingTestStage = z.infer<typeof benchPairingTestStageSchema>;
export type BenchPairingTestDevice = z.infer<typeof benchPairingTestDeviceSchema>;
export type BenchPairingTestOptions = z.infer<typeof benchPairingTestOptionsSchema>;

/**
 * SIL-2000. Body of POST /api/client/bench/pairings/:userCode/approve. Absent, undefined or [] approves with every
 * test (the pre-SIL-2000 behavior). Each entry's deviceKey is the opaque `device:<deviceIndex>` from testOptions (the API
 * refuses a real deviceKey as selection_invalid). At most MAX_STAGE_DESELECTIONS entries and MAX_STAGE_DESELECTIONS_BYTES bytes,
 * the limits of the approve DEFINER function; a repeated (deviceKey, profileId, stageIndex) is refused here as it is there.
 */
/** SIL-2246 R1. Closed vocabulary of plan areas and step kinds on the wire (the catalog is owner-written; see bench_plan_area). */
const planAreaSchema = z.string().regex(/^[a-z_]{2,24}$/);
const planStepKindSchema = z.string().regex(/^[a-z_]{2,32}$/);
const sha256HexSchema = z.string().regex(/^[0-9a-f]{64}$/);

/** SIL-2246 R1: `plan` of the approve body. `previewDigest` covers plan id+version, mandatory set version + effectiveAt, the discovery digest, the deselections and the resolution. */
export const benchPlanSelectionRequestSchema = z
  .object({
    planId: z.string().uuid(),
    version: z.number().int().positive(),
    mandatorySetVersion: z.number().int().positive(),
    previewDigest: sha256HexSchema,
  })
  .strict();
export type BenchPlanSelectionRequest = z.infer<typeof benchPlanSelectionRequestSchema>;

export const benchPairingApproveRequestSchema = z
  .object({
    deselections: z
      .array(benchStageDeselectionSchema)
      .max(MAX_STAGE_DESELECTIONS)
      .refine((l) => benchDeselectionsByteSize(l) <= MAX_STAGE_DESELECTIONS_BYTES, {
        message: 'deselections are too large',
      })
      .refine((l) => new Set(l.map((d) => JSON.stringify([d.deviceKey, d.profileId, d.stageIndex]))).size === l.length, {
        message: 'duplicate deselection',
      })
      .optional(),
    // SIL-2133 R2 (owner decision O2: opt-in per run, at approval). Admin-only; the route sends the pair and the DATABASE reads
    // the acknowledgment hash from bench_boot_tool_ack_text and accepts only the CURRENT version. The client never sends text
    // or a hash. At most 8, no duplicate toolId (the DB refuses both as invalid_argument too).
    toolOptIns: z
      .array(z.object({ toolId: benchBootToolIdSchema, ackVersion: z.string().regex(/^[a-z0-9.-]{1,32}$/) }).strict())
      .max(8)
      .refine((l) => new Set(l.map((t) => t.toolId)).size === l.length, { message: 'duplicate toolId' })
      .optional(),
    // SIL-2246 R1: the plan chosen at approval. The client NEVER sends resolution rows: the route recomputes the resolution and
    // refuses a `previewDigest` mismatch with 409 `preview_stale`. Gated by BENCH_PLANS_ENABLED (409 `plans_disabled`).
    plan: benchPlanSelectionRequestSchema.optional(),
    // SIL-2458: the approver's explicit "record inventory only, test nothing". Without it a plan that resolves to NO runnable
    // step is refused 409 `no_runnable_test`. Only meaningful with `plan`; the plan-less legacy approval runs every active profile.
    acknowledgeNoTests: z.boolean().optional(),
  })
  // A typo such as `deselection` would otherwise be stripped silently and approve at full scope: say so instead.
  .strict();
export type BenchPairingApproveRequest = z.infer<typeof benchPairingApproveRequestSchema>;

/**
 * SIL-2133 R2. One bootable tool the approval screen may offer, from GET /api/client/bench/pairings/:userCode while
 * BENCH_TOOL_STAGE_ENABLED is on. `ackText` is the acknowledgment the checkbox quotes for `ackVersion`; the approve call sends
 * `{toolId, ackVersion}` only. Never carries the profile's hardware match, the pin or an image (security N-d).
 */
export const benchPairingToolOptionSchema = z.object({
  toolId: benchBootToolIdSchema,
  label: z.string(),
  description: z.string(),
  ackVersion: z.string(),
  ackText: z.string(),
  available: z.boolean(),
  blockedReason: z.string().optional(),
});
export type BenchPairingToolOption = z.infer<typeof benchPairingToolOptionSchema>;

/** SIL-2133 R2. A tool stage of a run as the run detail shows it (from the write-once decision and opt-in rows). */
export interface BenchRunToolStageView {
  toolId: string;
  label: string;
  state: 'offered' | 'skipped' | 'not_opted_in' | 'unavailable';
  /** Neutral wording of the skip reason; absent unless skipped. */
  reason?: string;
  ack?: { by: string; at: string; version: string };
}

/** SIL-2493. The agent's last report for a running run. */
export interface BenchRunProgressView {
  /** When the agent was last heard from (ISO). The agent reports every 60 s and the API stores at most one per 50 s. */
  lastSeenAt: string;
  /** The stage the agent says is running and that has no stored result yet; absent between stages. */
  stage?: {
    deviceKey: string;
    stageName: string;
    startedAt: string;
    timeoutSeconds: number;
  };
}

/** SIL-2000. One optional stage the approving admin removed from a device's requirement. */
export interface BenchRunDeselectionView {
  deviceKey: string;
  profileId: string;
  version: number;
  stageIndex: number;
  /** null when the profile version no longer carries the stage name. */
  stageName: string | null;
}

// ---------------------------------------------------------------------------
// SIL-1708 (E-22): attach a freshly booted machine to a running run. The serials, device keys and IP shown to the approver
// are AGENT CLAIMS (pair/start is unauthenticated), never attested facts; the controls are the human approval and, for
// registered_bench runs only, the site IP.
// ---------------------------------------------------------------------------

/**
 * Body of POST /api/client/bench/pairings/:userCode/attach. STRICT: any other key (a `deselections` list, a typo) is refused
 * with one fixed 422; an attach never carries a test selection, and silently dropping it would read as "approved as chosen".
 */
export const benchPairingAttachRequestSchema = z.object({ runId: z.string().uuid() }).strict();
export type BenchPairingAttachRequest = z.infer<typeof benchPairingAttachRequestSchema>;

/** Largest bigint the database accepts for an event id (2^63 - 1). A larger decimal string would raise 22003 (a 500). */
const MAX_PG_BIGINT = BigInt('9223372036854775807');

/**
 * Body of POST /api/client/bench/runs/:id/reuse-acknowledgements. The event id is a bigint, so it travels as a decimal STRING
 * (a JSON number loses precision above 2^53 and is refused here). Bounded to the bigint range: the cast happens in the database.
 */
export const benchReuseAckRequestSchema = z
  .object({
    expectedLatestReuseEventId: z
      .string()
      .regex(/^[1-9]\d{0,18}$/)
      // zod runs a refinement even after the regex above failed, and BigInt('1.5') / BigInt('') throw: guard it again here.
      .refine((v) => /^[1-9]\d{0,18}$/.test(v) && BigInt(v) <= MAX_PG_BIGINT, { message: 'event id out of range' }),
  })
  .strict();
export type BenchReuseAckRequest = z.infer<typeof benchReuseAckRequestSchema>;

/**
 * SIL-1719 (E-33): a member's pass/fail for one `collect: operator` stage (POST /api/client/bench/runs/:id/collected-results).
 * There is no class, environment, recorder, device or org field: the database assigns every one of them. `.strict()`.
 * A correction that moves a recorded fail/timeout to pass needs a non-blank note (the database refuses it otherwise); the
 * portal asks for it before the round trip. No photo in v1.
 */
export const benchOperatorEntryRequestSchema = z
  .object({
    profileId: z.string().min(1).max(256).regex(/^[\x20-\x7e]+$/),
    stageIndex: z.number().int().nonnegative().max(65535),
    status: z.enum(['pass', 'fail']),
    note: z.string().max(1000).optional(),
  })
  .strict();
export type BenchOperatorEntryRequest = z.infer<typeof benchOperatorEntryRequestSchema>;

/** One stored operator result of a stage (the whole correction history is shown, oldest first). */
export interface BenchCollectResultView {
  seq: number;
  status: string;
  /** Server time the result was received (ISO). */
  recordedAt: string;
  /** True when the caller recorded it. The recorder is never named. */
  byMe: boolean;
  /** The row grading uses for the stage (the newest). */
  isLatest: boolean;
  note: string | null;
}

export interface BenchCollectStageView {
  profileId: string;
  stageIndex: number;
  stageName: string;
  /** Oldest first. Empty: no result yet. */
  results: BenchCollectResultView[];
}

/**
 * What a landed attach does (spec 5.4 l.191, bench_attach_segment). The ONLY source of the consequence copy the approval
 * screen shows: pinned by a shared test and by the API end-to-end test (each id has an observable effect there), so the text
 * cannot drift from the database. Per-segment stage claims (SIL-1924) are NOT touched by an attach.
 */
export const BENCH_ATTACH_EFFECTS = [
  { id: 'void_open_tickets', text: 'Voids every open wipe ticket of the run.' },
  {
    id: 'revoke_unticketed_authorizations',
    text: 'Revokes every wipe authorization that has no wipe ticket bound to it (a voided ticket counts as none). An org admin must authorize those again.',
  },
  { id: 'rotate_session_and_wipe_code', text: 'Replaces the run session and the wipe code. The previous agent session stops working.' },
  { id: 'close_live_segment', text: 'Closes the live segment: an armed resume token expires and an active session is superseded.' },
  {
    id: 'new_repair_attach_segment',
    text: 'Starts a new recovery segment from the new boot. The run keeps its evidence and its original 24 hour limit.',
  },
] as const;
export type BenchAttachEffectId = (typeof BENCH_ATTACH_EFFECTS)[number]['id'];

/** After approval the agent's next poll lands the attach; an approval older than this is dead (pairing_expired). */
export const BENCH_ATTACH_LANDS_WITHIN_SECONDS = 600;

/** Which of the three presented serial fields equals the run's stored identity (the database's `matched_fields`). */
export type BenchAttachMatchedField = 'productSerial' | 'chassisSerial' | 'boardSerial';

/**
 * Computed on the server, never by eye: `same` = both board serials are usable and equal after the database's normalization
 * (trim, upper-case); `different` = both usable and not equal, or either carries a character outside printable ASCII (a
 * homoglyph, zero-width or bidi character can only be an attack); `missing` = either side has no usable board serial.
 */
export type BenchBoardSerialMatch = 'same' | 'different' | 'missing';

const PRINTABLE_ASCII = /^[\x20-\x7e]*$/;

/** Same normal form as bench_normalize_chassis_serial on its API domain (printable ASCII): null = unusable, else upper(trim). */
function normalizeBenchSerial(raw: string | null | undefined): string | null {
  if (raw == null || !isUsableSerial(raw)) return null;
  return raw.trim().toUpperCase();
}

/**
 * One serial field, run vs presented. `absent` = neither side has a usable value (nothing to compare, not a concern);
 * `missing` = exactly one side has one; `different` = both usable and unequal, or either carries a character outside
 * printable ASCII (a homoglyph, zero-width or bidi character can only be an attack, and a human cannot read it reliably).
 */
export type BenchSerialCompare = 'same' | 'different' | 'missing' | 'absent';

export function compareBenchSerial(runSerial: string | null, presentedSerial: string | null): BenchSerialCompare {
  const odd = (v: string | null) => v !== null && !PRINTABLE_ASCII.test(v);
  if (odd(runSerial) || odd(presentedSerial)) return 'different';
  const run = normalizeBenchSerial(runSerial);
  const presented = normalizeBenchSerial(presentedSerial);
  if (run === null && presented === null) return 'absent';
  if (run === null || presented === null) return 'missing';
  return run === presented ? 'same' : 'different';
}

/** The board serial only: `absent` reads as `missing` (the approver needs a board serial to corroborate the machine). */
export function compareBenchBoardSerial(runBoardSerial: string | null, presentedBoardSerial: string | null): BenchBoardSerialMatch {
  const r = compareBenchSerial(runBoardSerial, presentedBoardSerial);
  return r === 'absent' ? 'missing' : r;
}

/** One running run the pending pairing could continue (client_bench_attachable_runs, plus the server-side comparisons). */
export interface BenchAttachCandidate {
  runId: string;
  startedAt: string;
  /** Claimed by the portal at approval (client_user has no email column). */
  approvedByEmail: string;
  attestationLevel: BenchAttestationLevel;
  /** Agent claim recorded with the run. */
  runBoardSerial: string | null;
  /** Agent claims from the new boot, exactly as presented. Escape on render. */
  presented: { productSerial: string | null; chassisSerial: string | null; boardSerial: string | null };
  /**
   * Which presented fields equal the run's stored IDENTITY (one normalized value, usually the product serial). At least one
   * (the database only lists runs that match), usually exactly one: a single match is normal and is NOT a warning by itself.
   */
  matchedFields: BenchAttachMatchedField[];
  /** Presented board serial vs the run's recorded board serial, computed in code. */
  boardSerialMatch: BenchBoardSerialMatch;
  /** Presented product / chassis serial vs the run's recorded value of the same field. `absent` = neither side has one. */
  productSerialCompare: BenchSerialCompare;
  chassisSerialCompare: BenchSerialCompare;
  pairingSourceIp: string;
  pairingAgeSeconds: number;
  /** The pairing IP is inside the run's OWN site. Always false for a self_reported run (no site to check). */
  inRunSite: boolean;
  crosscheckSubjects: string[];
  /** False when the run recorded no NVMe drive key: nothing was compared, so an empty diff is NOT a match. */
  nvmeCompared: boolean;
  nvmeRunKeyCount: number;
  nvmePresentedKeyCount: number;
  /** Run drive keys the new boot did not present. Agent claims, escape on render. Capped; a non-empty list never becomes empty. */
  nvmeKeysMissing: string[];
  /** Drive keys the new boot presented that the run never saw. */
  nvmeKeysNew: string[];
  /** The drive-key lists (missing / new) were cut at the cap. Says nothing about crosscheckSubjects, see crosscheckTruncated. */
  nvmeKeysTruncated: boolean;
  /** crosscheckSubjects was cut at the cap (SIL-1708 N3: its own flag, so a cut cross-check list is not reported as cut drives). */
  crosscheckTruncated: boolean;
}

/**
 * Security B1: reasons the approver must tick a SECOND confirmation before "Continue this run" is enabled. The code-provenance
 * checkbox ("I read this code from the screen of the machine in front of me") is always required on top of these.
 */
export type BenchAttachWarning = 'board_serial_not_same' | 'other_serial_differs' | 'self_reported' | 'outside_run_site' | 'old_code';

/** A code older than this has had time to be relayed to someone who is not at the machine. */
export const BENCH_ATTACH_OLD_CODE_SECONDS = 300;

/**
 * The database only needs ONE presented serial to equal the run's identity, so a tampered agent can present the right
 * product serial next to invented others. `other_serial_differs` fires when the product or chassis serial is `different` or
 * `missing` against the run's own record (`absent` and `same` are fine); the board serial has its own warning.
 */
export function benchAttachWarnings(c: BenchAttachCandidate): BenchAttachWarning[] {
  const w: BenchAttachWarning[] = [];
  if (c.boardSerialMatch !== 'same') w.push('board_serial_not_same');
  const bad = (r: BenchSerialCompare) => r === 'different' || r === 'missing';
  if (bad(c.productSerialCompare) || bad(c.chassisSerialCompare)) w.push('other_serial_differs');
  if (c.attestationLevel === 'self_reported') w.push('self_reported');
  else if (!c.inRunSite) w.push('outside_run_site');
  if (c.pairingAgeSeconds > BENCH_ATTACH_OLD_CODE_SECONDS) w.push('old_code');
  return w;
}

/** `error.code` of a non-2xx attach response is a key of this map (or the generic `not_found` / `too_many_attempts` of every pairing route). */
export const BENCH_ATTACH_REFUSAL_MESSAGES = {
  attach_disabled: 'Attaching a machine to a running run is not enabled yet.',
  invalid_body: 'invalid attach request',
  payload_too_large: 'The attach request is too large.',
  not_authorized: 'You are not allowed to attach to that run.',
  // N6: no reasons. A member cannot act on them, and naming them would tell a member the run's wipe-authorization state.
  admin_required: 'Only an org admin can attach this machine to that run.',
  attach_rate_limited: 'Too many attach approvals for that run in the last hour. Try again later.',
  invalid_attach: 'The attach request could not be processed.',
  run_not_found: 'That run was not found.',
  run_not_running: 'That run is no longer running.',
  run_expired: 'That run has passed its 24 hour limit and cannot be continued.',
  serial_unusable: 'The machine presented no usable serial number.',
  identity_mismatch: 'The machine does not match that run.',
  ambiguous_serial: 'Two running runs share this machine identity. Finish or abandon one of them first.',
  discovery_mismatch: 'The hardware this machine reports differs from that run.',
  site_mismatch: 'The machine is not on the network of the bench site that run started on.',
  ticket_issued: 'A wipe ticket was already issued for that run, so it cannot continue in WinPE.',
  ticket_recent: 'A wipe started on that run in the last 10 minutes. Wait for it to finish, then try again.',
  segment_cap: 'That run has reached its limit of boots.',
  try_again: 'The system is busy. Try again in a moment.',
} as const;
export type BenchAttachRefusalCode = keyof typeof BENCH_ATTACH_REFUSAL_MESSAGES;

/** 200 body of POST /pairings/:userCode/attach. The approval is recorded; the machine lands it at its next check-in. */
export interface BenchAttachResponse {
  runId: string;
  status: 'approved';
  landsWithinSeconds: typeof BENCH_ATTACH_LANDS_WITHIN_SECONDS;
}

/** 200 body of POST /runs/:id/attach/cancel. */
export interface BenchAttachCancelResponse {
  cancelledCount: number;
}

/** 200 body of POST /runs/:id/reuse-acknowledgements. */
export interface BenchReuseAckResponse {
  acknowledgedCount: number;
}

/** One segment (one boot of the machine) of a run. No discovery, image id, token or IP: those columns are not granted to the portal. */
export interface BenchRunSegmentView {
  segmentIdx: number;
  environment: 'linux' | 'winpe';
  /** `uefi` for a foreign visit (a handoff to the UEFI step), else null. */
  foreignVisit: string | null;
  status: 'armed' | 'active' | 'done' | 'expired' | 'superseded';
  /** initial | handoff | repair_attach (an attach shows as recovery). */
  reason: string;
  armedAt: string | null;
  expiresAt: string | null;
  consumedAt: string | null;
  closedAt: string | null;
  closedReason: string | null;
  /** An agent claim (the bench agent reports it; bounded to 64 chars by pair/start). Escape on render. */
  agentVersion: string | null;
  createdAt: string;
}

/** One audit event of a run: kind, reason and time only. The portal holds no grant on detail, subject or source IP. */
export interface BenchRunEventView {
  /** bigint as a decimal string. */
  id: string;
  /** Unknown kinds must render generically. */
  kind: string;
  reason: string | null;
  segmentIdx: number | null;
  byYou: boolean;
  createdAt: string;
}

/**
 * Unacknowledged `resume_reuse` state of a running run (a resume token presented again: a possible cloned NVRAM or USB token).
 * Computed with its own aggregate queries, never from the capped event list. While `open`, the commit is held.
 */
export interface BenchRunReuseView {
  open: boolean;
  /** Quote this as expectedLatestReuseEventId when acknowledging (string; null when none is open). */
  latestReuseEventId: string | null;
  /** Unacknowledged resume_reuse events. */
  count: number;
  /** The newest unacknowledged events (at most 50), oldest first: id and time, so the acknowledging admin sees each one. */
  events: Array<{ id: string; createdAt: string }>;
}

/** An attach approved in the last 10 minutes and not yet landed, refused or cancelled. Cancel is authoritative; this drives the button. */
export interface BenchRunPendingAttachView {
  approvedAt: string;
  byYou: boolean;
}

/**
 * SIL-2113. A one-time BMC boot override that may still be on the unit (migration 1155 v_bench_bmc_override_findings, read
 * through the client_readonly COLUMN grant). `reset_failed`: the tool stopped because it could not prove the override was
 * cleared (do not reboot yet). `may_remain`: the run ended before the tool confirmed it was cleared. Never carries a code,
 * address or account; the portal renders static copy for each state.
 */
export interface BenchRunBmcOverrideView {
  state: 'reset_failed' | 'may_remain';
  observedAt: string;
  segmentIdx: number;
}

export interface BenchRunDetail extends BenchRunSummary {
  /**
   * SIL-2000, additive: `partial` when an org admin deselected at least one test at approval (bench_run.selection_scope).
   * Such a run grades the deselected devices, and the chassis, at no better than normal_enterprise_wear (never like_new).
   * Absent from an API that predates it; the portal reads absent as `full`.
   */
  scope?: 'full' | 'partial';
  /** SIL-2000, additive: the stages removed at approval (bench_run_stage_deselection). Absent = none. */
  deselections?: BenchRunDeselectionView[];
  /**
   * SIL-2133 R2, additive: the tool stages of the run (state, neutral skip wording, who acknowledged and when), from the
   * write-once decision and opt-in rows. Shown whatever the kill switch says; absent when the run holds neither.
   */
  toolStages?: BenchRunToolStageView[];
  /**
   * SIL-2493, additive: what the agent last reported while the run is `running` (bench_run_progress). Absent for a finished run,
   * and for a run whose agent predates progress reports (the portal then shows nothing new). The stage text is agent-supplied.
   */
  progress?: BenchRunProgressView;
  /** Caller is an org admin AND the run is still running. UX only; the DEFINER function is the gate. */
  canAuthorizeWipe: boolean;
  /** Device keys whose latest authorization event is 'authorize'. */
  wipeAuthorizedDeviceKeys: string[];
  /**
   * SIL-1682. Subset of wipeAuthorizedDeviceKeys whose current authorization already has its
   * single wipe ticket (the wipe was started). Not a call to re-authorize: a finished wipe is
   * the normal outcome; an orphan ticket (no stage recorded) needs the machine re-paired.
   */
  wipeSpentDeviceKeys: string[];
  /** Device keys whose latest authorization event is 'revoke' (wipe declined; the agent skips them). */
  wipeDeclinedDeviceKeys: string[];
  devices: BenchRunDeviceView[];
  stages: BenchRunStageView[];
  wipeAuthorizations: BenchWipeAuthorizationView[];
  certificates: BenchCertificateView[];
  /** SIL-2258, additive: the approved plan's coverage of this run. Absent from an API that predates it and for a run with no plan. */
  planCoverage?: BenchPlanCoverageView;
  /** SIL-2502, additive: planned steps that did not run (plan gaps), with the stored reason. Absent for a run with no plan or an API that predates it. */
  notRunSteps?: BenchNotRunStepView[];
  /** Newest first; [0] is the current grade. */
  cosmeticGrades: BenchCosmeticGradeView[];
  /** SIL-1708, additive (absent from an API that predates it): every segment of the run, oldest first. */
  segments?: BenchRunSegmentView[];
  /** SIL-1708, additive: the newest 200 events, newest first. See eventsTruncated. */
  events?: BenchRunEventView[];
  /** True when the run has more events than `events` carries. */
  eventsTruncated?: boolean;
  /** SIL-1708, additive: unacknowledged resume_reuse state (own aggregate, not derived from `events`). */
  reuse?: BenchRunReuseView;
  /** SIL-1708, additive: an attach approval waiting for the machine's next check-in, or null. */
  pendingAttach?: BenchRunPendingAttachView | null;
  /**
   * SIL-2113, additive (absent from an API that predates it, or when the view is not applied: the portal reads absent as none):
   * a one-time BMC boot override that may still be on the unit, or null. Own aggregate, never derived from `events`.
   */
  bmcOverride?: BenchRunBmcOverrideView | null;
}

// ---------------------------------------------------------------------------
// SIL-2246 R1: bench diagnostic plans (customer side). All enums closed, all strings and lists bounded.
// ---------------------------------------------------------------------------

/** One discovered part as the approval page shows it. `key` is opaque `device:<index>`; the serial is masked server-side. */
export const benchHardwareFoundPartSchema = z.object({
  key: z.string().regex(/^device:(0|[1-9]\d{0,8})$/),
  kind: benchDeviceKindSchema,
  area: planAreaSchema,
  vendor: z.string().max(128).nullable(),
  model: z.string().max(128).nullable(),
  serialMasked: z.string().max(128).nullable(),
});

/** `hardwareFound` of the pairing lookup: host identity plus parts, so the buyer can pick a plan that fits. */
export const benchHardwareFoundSchema = z.object({
  host: z.object({
    vendor: z.string().max(128).nullable(),
    model: z.string().max(128).nullable(),
    serviceTag: z.string().max(128).nullable(),
  }),
  parts: z.array(benchHardwareFoundPartSchema).max(512),
});
export type BenchHardwareFound = z.infer<typeof benchHardwareFoundSchema>;

/** `planSelection` of the pairing lookup. `supported` = the agent declared `planAssignments` (pair/start `capabilities`). */
export const benchPlanSelectionStateSchema = z.object({
  required: z.boolean(),
  defaultPlan: z.object({ id: z.string().uuid(), version: z.number().int().positive() }).optional(),
  supported: z.boolean(),
});
export type BenchPlanSelectionState = z.infer<typeof benchPlanSelectionStateSchema>;

export const benchPlanOriginSchema = z.enum(['mandatory', 'org', 'builtin']);
export const benchPlanStepStatusSchema = z.enum(['resolved', 'builtin', 'gap']);
export const benchPlanGapReasonSchema = z.enum([
  'unknown_kind',
  'inactive_kind',
  'kind_area_mismatch',
  'not_org_addable',
  'not_mandatory_allowed',
  'no_profile',
  'no_stage',
  'not_agent_observed',
  'broken_profile',
  'identity_mismatch',
  'missing_class',
]);
export type BenchPlanGapReason = z.infer<typeof benchPlanGapReasonSchema>;

export const benchPlanSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string().max(128),
  version: z.number().int().positive(),
  areas: z.array(planAreaSchema).max(16),
  origin: z.enum(['siliconx', 'org']),
  updateAvailable: z.boolean(),
});
export type BenchPlanSummary = z.infer<typeof benchPlanSummarySchema>;

export const benchPlanDetailSchema = benchPlanSummarySchema.extend({
  steps: z.array(z.object({ area: planAreaSchema, stepKind: planStepKindSchema })).max(256),
});
export type BenchPlanDetail = z.infer<typeof benchPlanDetailSchema>;

/** One row of GET /api/client/bench/plans: a summary plus whether the plan wipes, and (with a pairing `code`) how it fits the hardware. */
export const benchPlanSearchResultSchema = benchPlanSummarySchema.extend({
  includesWipe: z.boolean(),
  fit: z
    .object({
      fits: z.boolean(),
      gaps: z.number().int().nonnegative(),
      outOfScope: z.number().int().nonnegative(),
    })
    .optional(),
});
export type BenchPlanSearchResult = z.infer<typeof benchPlanSearchResultSchema>;

/** Org plan authoring (admin). Mirrors bench_org_plan_save: v1 steps carry no params; `inventory` is forced in scope by the definer. */
const planReasonSchema = z.string().trim().min(1).max(500);
const planAreasBodySchema = z.array(z.object({ area: planAreaSchema, inScope: z.boolean() }).strict()).max(16);
const planStepsBodySchema = z.array(z.object({ area: planAreaSchema, stepKind: planStepKindSchema }).strict()).max(64);
export const benchOrgPlanVersionRequestSchema = z
  .object({ areas: planAreasBodySchema, steps: planStepsBodySchema, reason: planReasonSchema.optional() })
  .strict();
export const benchOrgPlanCreateRequestSchema = benchOrgPlanVersionRequestSchema
  .extend({
    name: z.string().min(1).max(120),
    originPlanId: z.string().uuid().optional(),
    originVersion: z.number().int().positive().optional(),
  })
  .strict()
  .refine((b) => (b.originPlanId === undefined) === (b.originVersion === undefined), { message: 'originPlanId and originVersion go together' });
export const benchOrgPlanArchiveRequestSchema = z.object({ reason: planReasonSchema.optional() }).strict();
export type BenchOrgPlanVersionRequest = z.infer<typeof benchOrgPlanVersionRequestSchema>;
export type BenchOrgPlanCreateRequest = z.infer<typeof benchOrgPlanCreateRequestSchema>;

export const benchMandatorySetViewSchema = z.object({
  version: z.number().int().positive(),
  effectiveAt: z.string().max(40),
  changeSummary: z.string().max(1000),
  items: z.array(z.object({ area: planAreaSchema, stepKind: planStepKindSchema })).max(256),
});
export type BenchMandatorySetView = z.infer<typeof benchMandatorySetViewSchema>;

/** Body of POST /api/client/bench/pairings/:userCode/plan-preview. */
export const benchPlanPreviewRequestSchema = z
  .object({
    planId: z.string().uuid(),
    version: z.number().int().positive(),
    deselections: z.array(benchStageDeselectionSchema).max(MAX_STAGE_DESELECTIONS).optional(),
  })
  .strict();
export type BenchPlanPreviewRequest = z.infer<typeof benchPlanPreviewRequestSchema>;

export const benchPlanPreviewStepSchema = z.object({
  deviceKey: z.string().regex(/^device:(0|[1-9]\d{0,8})$/),
  stepKind: planStepKindSchema,
  origin: benchPlanOriginSchema,
  status: benchPlanStepStatusSchema,
  gapReason: benchPlanGapReasonSchema.optional(),
  destructive: z.boolean(),
  estSeconds: z.number().int().nonnegative(),
  /** An org step the approver unticked (O6): shown, but not run and not counted. */
  deselected: z.boolean().optional(),
});

/** What approving the plan would do, as the page shows it. The digest is what approve must echo. */
export const benchPlanPreviewSchema = z.object({
  planId: z.string().uuid(),
  version: z.number().int().positive(),
  mandatorySetVersion: z.number().int().positive(),
  previewDigest: sha256HexSchema,
  steps: z.array(benchPlanPreviewStepSchema).max(4096),
  outOfScope: z.number().int().nonnegative(),
  gaps: z.number().int().nonnegative(),
  wipeDevices: z.array(z.string().regex(/^device:(0|[1-9]\d{0,8})$/)).max(512),
  estimatedSeconds: z.number().int().nonnegative(),
  /**
   * SIL-2458: steps that will actually run a stage on this machine (resolved by an active profile, not unticked). 0 = the run would
   * commit with no tests (inventory only): the page blocks approval behind an acknowledgment and the API answers 409
   * `no_runnable_test` without `acknowledgeNoTests`. Optional so a cached older response still parses (absent = unknown, not 0).
   * Not part of `previewDigest` (it is derived from the resolution, which the digest already covers).
   */
  runnableSteps: z.number().int().nonnegative().optional(),
  /**
   * SIL-1818 D2: present ONLY when the selected plan, on this machine, assigns a stage that needs a GPU tool pack AND packs are
   * on for the org AND a current disclosure is published. The page then shows that disclosure version ("a signed NVIDIA driver is
   * loaded temporarily and does not persist; the site's egress IP and timing reach NVIDIA when user space is downloaded") and
   * approving the plan is the consent. Not part of `previewDigest` (it is derived, not chosen). Absent for every other plan.
   */
  gpuPackDisclosure: z.object({ version: z.string().regex(/^[a-z0-9.-]{1,32}$/) }).optional(),
  /**
   * SIL-2330. The asset-creating devices (chassis, GPU: the only kinds the ingest turns into assets) and what approving would do to
   * each against the org's inventory: `new` asset or `existing` (already in the inventory, will be updated).
   * A serial held by another org reads as `new`: the preview never reveals another org's inventory. Not part of `previewDigest`. Absent when it could not be computed.
   */
  assetImpact: z
    .array(
      z.object({
        deviceKey: z.string().regex(/^device:(0|[1-9]\d{0,8})$/),
        kind: z.enum(['chassis', 'gpu']),
        status: z.enum(['new', 'existing']),
      }),
    )
    .max(512)
    .optional(),
});
export type BenchPlanPreview = z.infer<typeof benchPlanPreviewSchema>;

/** Run detail / label coverage under a plan. */
export const benchPlanCoverageSchema = z.object({
  planName: z.string().max(128),
  planVersion: z.number().int().positive(),
  mandatorySetVersion: z.number().int().positive(),
  outOfScope: z.number().int().nonnegative(),
  gaps: z.number().int().nonnegative(),
});
export type BenchPlanCoverage = z.infer<typeof benchPlanCoverageSchema>;

// SIL-2246 R1: ops DTOs.
export const benchMandatoryPreviewSchema = z.object({
  added: z.array(z.object({ area: planAreaSchema, stepKind: planStepKindSchema })).max(256),
  removed: z.array(z.object({ area: planAreaSchema, stepKind: planStepKindSchema })).max(256),
  changed: z.array(z.object({ area: planAreaSchema, stepKind: planStepKindSchema })).max(256),
  lowersCoverage: z.boolean(),
  affected: z.object({
    plans: z.number().int().nonnegative(),
    orgs: z.number().int().nonnegative(),
    runsPerWeek: z.number().int().nonnegative(),
  }),
  addedSecondsPerRun: z.number().int(),
  perOrgTop: z.array(z.object({ orgId: z.string().uuid(), plans: z.number().int().nonnegative() })).max(20),
});
export type BenchMandatoryPreview = z.infer<typeof benchMandatoryPreviewSchema>;

export const benchPlanGapRowSchema = z.object({
  planId: z.string().uuid(),
  planVersion: z.number().int().positive(),
  area: planAreaSchema,
  stepKind: planStepKindSchema,
  reason: benchPlanGapReasonSchema,
  devices: z.number().int().nonnegative(),
});
export type BenchPlanGapRow = z.infer<typeof benchPlanGapRowSchema>;
